import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  LabelList,
} from "recharts";
import { SupplierResponsiveness as SupplierResponsivenessType } from "../../../types/contractDashboard";
import styles from "./supplierResponsiveness.module.scss";

interface SupplierResponsivenessProps {
  data: SupplierResponsivenessType[];
}

const SupplierResponsiveness: React.FC<SupplierResponsivenessProps> = ({
  data,
}) => {
  const { chartData, target } = useMemo(() => {
    const targetHours = 24;
    const suppliers = data.map((supplier) => ({
      name: supplier.supplier_name,
      hours: supplier.response_time_hours,
      target: targetHours,
      status: supplier.response_time_hours <= targetHours ? "good" : "poor",
    }));

    return { chartData: suppliers, target: targetHours };
  }, [data]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const hours = payload[0].value;
      const isGood = hours <= target;

      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "10px 14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#111827", fontWeight: 600, mb: 0.5, fontSize: "13px" }}
          >
            {payload[0].payload.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            {isGood ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: "#10b981" }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 16, color: "#ef4444" }} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: isGood ? "#10b981" : "#ef4444",
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              {hours}h
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", fontSize: "11px" }}
          >
            {isGood
              ? `${target - hours}h under target`
              : `${hours - target}h over target`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom label to show hours with icon
  const CustomLabel = (props: any) => {
    const { x, y, width, value, payload } = props;
    const isGood = value <= target;

    return (
      <g>
        <text
          x={x + width + 8}
          y={y + 14}
          fill={isGood ? "#10b981" : "#ef4444"}
          fontSize={12}
          fontWeight={600}
        >
          {value}h
        </text>
      </g>
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Typography variant="h6" className={styles.title}>
            Supplier Responsiveness
          </Typography>
          <Typography variant="h6" sx={{ color: "#6b7280", fontSize: "16px", fontWeight: 600 }}>
            Target: 24h
          </Typography>
        </Box>
        <Typography variant="body2" className={styles.subtitle}>
          Average time to resolve supply or quality issues (hours).
        </Typography>
      </Box>

      <Box className={styles.chartContainer} sx={{ position: "relative" }}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 50, left: 5, bottom: 10 }}
            barCategoryGap="12%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={{ stroke: "#d1d5db" }}
              tickLine={false}
              domain={[0, "dataMax + 10"]}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: "#374151", fontSize: 11, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.03)" }}
              wrapperStyle={{ zIndex: 1000 }}
            />
            <ReferenceLine
              x={target}
              stroke="#6b7280"
              strokeDasharray="5 5"
              strokeWidth={1.5}
            />
            <Bar dataKey="hours" radius={[0, 6, 6, 0]} barSize={26}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.status === "good" ? "#10b981" : "#ef4444"}
                  opacity={0.9}
                />
              ))}
              <LabelList dataKey="hours" content={<CustomLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default SupplierResponsiveness;

